<template>
    <div v-if="loaded" class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton class="success" @click="saveClick()"><i class="fa-solid fa-floppy-disk"></i>
                {{ $LANG.Get('buttons.save') }}
            </NevsButton>
        </div>
        <NevsCard class="form schedule-card">
            <h3 class="working-hours-heading">{{ $LANG.Get('labels.sportsCenterWorkingHoursHeading') }}</h3>
            <div class="schedule">
                <div v-for="day in weekdays" :key="day.value" class="schedule-day">
                    <div class="schedule-day-name">{{ day.label }}</div>
                    <div class="schedule-day-body">
                        <div v-for="(interval, key) in workingHours[day.value]" :key="key" class="schedule-interval">
                            <i class="fa-regular fa-clock schedule-interval-icon"></i>
                            <NevsMaskedField v-model="interval.from" :mask="'NN:NN'" :width="'76px'"></NevsMaskedField>
                            <span class="schedule-interval-dash">–</span>
                            <NevsMaskedField v-model="interval.to" :mask="'NN:NN'" :width="'76px'"></NevsMaskedField>
                            <span :title="$LANG.Get('tooltips.delete')" class="schedule-interval-remove"
                                  @click="removeInterval('workingHours', day.value, key)"><i class="fa-solid fa-xmark"></i></span>
                        </div>
                        <span v-if="workingHours[day.value].length === 0" class="schedule-empty">
                            {{ $LANG.Get('labels.closed') }}
                        </span>
                        <span class="schedule-add" @click="addInterval('workingHours', day.value)">
                            <i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addInterval') }}
                        </span>
                    </div>
                </div>
            </div>
        </NevsCard>
        <NevsCard class="form schedule-card">
            <h3 class="working-hours-heading">{{ $LANG.Get('labels.receptionWorkingHoursHeading') }}</h3>
            <div class="schedule">
                <div v-for="day in weekdays" :key="day.value" class="schedule-day">
                    <div class="schedule-day-name">{{ day.label }}</div>
                    <div class="schedule-day-body">
                        <div v-for="(interval, key) in receptionWorkingHours[day.value]" :key="key" class="schedule-interval">
                            <i class="fa-regular fa-clock schedule-interval-icon"></i>
                            <NevsMaskedField v-model="interval.from" :mask="'NN:NN'" :width="'76px'"></NevsMaskedField>
                            <span class="schedule-interval-dash">–</span>
                            <NevsMaskedField v-model="interval.to" :mask="'NN:NN'" :width="'76px'"></NevsMaskedField>
                            <span :title="$LANG.Get('tooltips.delete')" class="schedule-interval-remove"
                                  @click="removeInterval('receptionWorkingHours', day.value, key)"><i class="fa-solid fa-xmark"></i></span>
                        </div>
                        <span v-if="receptionWorkingHours[day.value].length === 0" class="schedule-empty">
                            {{ $LANG.Get('labels.closed') }}
                        </span>
                        <span class="schedule-add" @click="addInterval('receptionWorkingHours', day.value)">
                            <i class="fa-solid fa-plus"></i> {{ $LANG.Get('buttons.addInterval') }}
                        </span>
                    </div>
                </div>
            </div>
        </NevsCard>
    </div>
</template>

<script>
import Client from "@/models/Client.js";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsMaskedField from "@/components/nevs/NevsMaskedField.vue";

// Ensures the schedule has an interval array for every day of week (ISO 1-7) plus the holiday row (8),
// cloning incoming intervals.
function normalizeSchedule(schedule) {
    let normalized = {};
    for (let day = 1; day <= 8; day++) {
        let intervals = (schedule && Array.isArray(schedule[day])) ? schedule[day] : [];
        normalized[day] = intervals.map((interval) => ({from: interval.from, to: interval.to}));
    }
    return normalized;
}

export default {
    name: "WorkingHours",
    components: {NevsMaskedField, NevsCard, NevsButton},
    data() {
        return {
            loaded: false,
            client: new Client(),
            weekdays: this.$HELPERS.GetWeekdays(this, true),
            workingHours: {},
            receptionWorkingHours: {}
        }
    },
    methods: {
        addInterval(schedule, dayKey) {
            this[schedule][dayKey].push({from: '08:00', to: '16:00'});
        },
        removeInterval(schedule, dayKey, index) {
            this[schedule][dayKey].splice(index, 1);
        },
        saveClick() {
            // Every interval (in either schedule) must be a valid HH:MM range with the start before the end.
            let timePattern = /^([01]\d|2[0-3]):[0-5]\d$/;
            for (let schedule of [this.workingHours, this.receptionWorkingHours]) {
                for (let day = 1; day <= 8; day++) {
                    for (let interval of schedule[day]) {
                        if (!timePattern.test(interval.from) || !timePattern.test(interval.to) || interval.from >= interval.to) {
                            this.$LOCAL_BUS.TriggerEvent('popup', {type: 'alert', text: this.$LANG.Get('labels.invalidWorkingHours')});
                            return;
                        }
                    }
                }
            }

            this.client.working_hours = this.workingHours;
            this.client.reception_working_hours = this.receptionWorkingHours;

            let vm = this;
            this.$API.APICall('put', 'client', this.client, (data, success) => {
                if (success) {
                    vm.$LOCAL_BUS.TriggerEvent('notification', {text: vm.$LANG.Get('alerts.recordSaved')});
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        }
    },
    mounted() {
        if (!this.$store.state.user.permissions.includes('ADMINISTRATOR')) {
            this.$LOCAL_BUS.TriggerEvent('popup', {text: this.$LANG.Get('alerts.unauthorized'), type: 'alert'});
            this.$router.push('/');
        }
        this.$store.commit('selectMenu', 'settings');
        this.$store.commit('selectSubMenu', 'workingHours');
        this.$store.commit('setBreadcrumbs', [
            {
                label: this.$LANG.Get('modules.settings'),
                link: null
            },
            {
                label: this.$LANG.Get('modules.workingHours'),
                link: null
            }
        ]);

        let vm = this;
        this.$API.APICall('get', 'client', {}, (data, success) => {
            if (success && data.client !== null) {
                vm.client.Fill(data.client);
                vm.workingHours = normalizeSchedule(vm.client.working_hours);
                vm.receptionWorkingHours = normalizeSchedule(vm.client.reception_working_hours);
            } else {
                vm.$HELPERS.HandleServerError(vm, data);
            }
            vm.$nextTick(() => {
                vm.loaded = true;
            });
        });
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.schedule-card {
    max-width: 720px;
}

.working-hours-heading {
    margin: 0 0 12px;
}

.schedule {
    border: 1px solid $sand-300;
    border-radius: 8px;
    overflow: hidden;
}

.schedule-day {
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid $sand-200;
}

.schedule-day:last-child {
    border-bottom: none;
}

.schedule-day-name {
    width: 130px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 12px 14px;
    font-weight: 600;
    color: $ink-strong;
    background-color: $sand-100;
    border-right: 1px solid $sand-200;
}

.schedule-day-body {
    flex-grow: 1;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
}

.schedule-interval {
    display: inline-flex;
    align-items: center;
    background-color: $peach-wash;
    border: 1px solid $sand-300;
    border-radius: 20px;
    padding: 3px 8px 3px 12px;
}

.schedule-interval-icon {
    color: $ink-muted;
    margin-right: 8px;
    font-size: 13px;
}

.schedule-interval-dash {
    margin: 0 6px;
    color: $ink-muted;
}

.schedule-interval :deep(.nevs-field) {
    margin: 0;
}

.schedule-interval :deep(.nevs-text-field) {
    text-align: center;
    padding: 4px 6px;
    border-radius: 6px;
}

.schedule-interval-remove {
    cursor: pointer;
    margin-left: 8px;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: $ink-faint;
    transition: background-color 0.15s, color 0.15s;
}

.schedule-interval-remove:hover {
    background-color: $danger-surface;
    color: $danger;
}

.schedule-empty {
    color: $ink-faint;
    font-style: italic;
    font-size: 13px;
}

.schedule-add {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    color: $primary;
    border: 1px dashed $sand-400;
    border-radius: 20px;
    padding: 4px 12px;
    transition: background-color 0.15s;
}

.schedule-add:hover {
    background-color: $peach-wash;
}
</style>
